import { useRef, useState } from 'react';
import { useGSAP } from '@gsap/react';
import { twJoin } from '@beerntea/pitcher-ui';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import type { UsatourDate } from '@pages/Usa/UsaPage.tsx';
import { runScramble } from '@/helpers/runScramble';
gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

import './shape.css';

type SpotProps = {
  tourDate: UsatourDate;
  onClick: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  isFocused: boolean;
};
export const TourLocation = ({ tourDate, onClick, onMouseEnter, onMouseLeave, isFocused }: SpotProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const ripple00 = useRef<HTMLDivElement>(null);
  const ripple01 = useRef<HTMLDivElement>(null);
  const ripple1 = useRef<HTMLDivElement>(null);
  const ripple2 = useRef<HTMLDivElement>(null);
  const timeline = useRef<gsap.core.Timeline | null>(null);
  const cityRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);

  useGSAP(() => {
    if (!ref.current) return;

    // Master timeline to control all animations together
    const masterTl = gsap.timeline({ paused: true });
    timeline.current = masterTl;

    // Main element pulse animation (yoyo)
    const pulseTl = gsap.timeline({ repeat: -1, yoyo: true });
    pulseTl.fromTo(
      ref.current,
      { scale: 0.1, opacity: 1 },
      {
        opacity: 1,
        scale: 1.2,
        duration: 0.6,
        ease: 'power2.out',
      },
    );
    masterTl.add(pulseTl, 0);

    // Ripple animations (no yoyo, just repeat)
    [ripple1.current, ripple2.current].forEach((el, i) => {
      if (!el) return;
      const rippleTl = gsap.timeline({ repeat: -1 });
      rippleTl.fromTo(
        el,
        { scale: 0.1, opacity: 1 },
        {
          scale: 3,
          rotate: 360,
          opacity: 0,
          duration: 1.6,
          ease: 'power2.out',
          delay: i * 0.8,
        },
      );
      masterTl.add(rippleTl, 0);
    });
  }, []);

  useGSAP(() => {
    if (isFocused || isHovered) {
      timeline.current?.play();
    } else {
      timeline.current?.pause();
      timeline.current?.seek(0);
    }
  }, [isFocused, isHovered]);

  const handleMouseEnter = () => {
    setIsHovered(true);
    onMouseEnter();
    if (cityRef.current) {
      gsap.fromTo(
        cityRef.current,
        { opacity: 1 },
        {
          opacity: 0,
          duration: 0.12,
          yoyo: true,
          repeat: 1,
          ease: 'power1.inOut',
          onComplete: () => {
            cityRef.current && (cityRef.current.style.opacity = '1');
          },
        },
      );
    }
  };
  const handleMouseLeave = () => {
    setIsHovered(false);
    onMouseLeave();
    if (cityRef.current) runScramble(cityRef.current, 0.4, tourDate.location);
  };

  const bottomAlign = tourDate.location == 'Washington';

  return (
    <div
      className={twJoin('tw-absolute tw-cursor-pointer')}
      style={{ top: `${tourDate.y}%`, left: `${tourDate.x}%` }}
      id={`spot-${tourDate.id}`}
      onMouseEnter={handleMouseEnter}
      onClick={onClick}
      onMouseLeave={handleMouseLeave}
    >
      <div className="tw-absolute -tw-translate-x-1/2 -tw-translate-y-1/2 tw-w-20 tw-h-20 tw-rounded-full tw-z-50"></div>
      <div
        ref={ripple01}
        className={twJoin('tw-absolute -tw-translate-x-1/2 -tw-translate-y-1/2 tw-origin-center', 'hexagon')}
        style={{ height: '32px', background: 'white', '--b': '4px' } as React.CSSProperties}
      />
      <div
        ref={ripple00}
        className={twJoin('tw-absolute -tw-translate-x-1/2 -tw-translate-y-1/2 tw-origin-center', 'hexagon')}
        style={{ height: '28px', background: 'black', '--b': '4px' } as React.CSSProperties}
      />
      <div
        ref={ripple1}
        className={twJoin('tw-absolute -tw-translate-x-1/2 -tw-translate-y-1/2 tw-origin-center', 'hexagon')}
        style={{ height: '20px', background: 'white', '--b': '8px' } as React.CSSProperties}
      />
      <div
        ref={ripple2}
        className={twJoin('tw-absolute -tw-translate-x-1/2 -tw-translate-y-1/2 tw-origin-center', 'hexagon')}
        style={{ height: '28px', background: 'white' }}
      />
      <div
        className={twJoin('tw-absolute -tw-translate-x-1/2 -tw-translate-y-1/2 tw-origin-center', 'hexagon')}
        style={{ height: '16px', background: 'white' }}
      />
      <div
        ref={ref}
        className={twJoin('tw-absolute -tw-translate-x-1/2 -tw-translate-y-1/2 tw-origin-center', 'hexagon')}
        style={{ height: '32px' }}
      />
      {/* City name centered underneath the point */}
      <div
        ref={cityRef}
        className={twJoin(
          'tw-absolute tw-left-1/2 tw-translate-x-[-50%]  tw-text-center tw-font-display tw-text-xs sm:tw-text-base tw-font-semibold tw-text-white tw-text-shadow tw-uppercase',
          bottomAlign ? 'tw-top-full tw-mt-6' : 'tw-bottom-full tw-mb-6',
        )}
      >
        {tourDate.location}
      </div>
    </div>
  );
};
